import * as React from "react";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { drawerTableWrapperClassName } from "../common/Table/TableStyles";
import { FlaggedPathSummary } from "../../models/FlaggedPath";
import JobsRepository from "../../repositories/JobsRepository";
import { UserRole } from "../../models/User";
import ActionConfirmationModal, { ACTION_CONFIGS } from "../common/ActionConfirmationModal";
import { ConfirmationModal } from "../common/ConfirmationModal";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface CreateExportFlaggedPathsToCsvTableProps {
  organizationId: string;
  newFlaggedPathsForExport: FlaggedPathSummary[];
  setNewFlaggedPathsForExport: React.Dispatch<React.SetStateAction<FlaggedPathSummary[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const CreateExportFlaggedPathsToCsvTable: React.FC<CreateExportFlaggedPathsToCsvTableProps> = ({
  organizationId,
  newFlaggedPathsForExport,
  setNewFlaggedPathsForExport,
  userRole,
  isAssignedUser,
}: CreateExportFlaggedPathsToCsvTableProps) => {
  const requiresReason = useRequiresReason();
  const [confirmExportModalOpen, setConfirmExportModalOpen] = useState<boolean>(false);
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  const handleExport = async (reason?: string) => {
    await jobsRepository.startCsvGenerationFromFlaggedPaths(
      organizationId,
      newFlaggedPathsForExport.map((p) => ({ id: p.id, path: p.path })),
      reason
    );
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_FLAGGED_PATHS, organizationId] });
    setNewFlaggedPathsForExport([]);
    setConfirmExportModalOpen(false);
    toast.success("Export started");
  };

  return (
    <div className="flex flex-col gap-2 pt-2 max-h-[40%] min-h-[40%]">
      <div className="flex flex-col gap-1">
        {requiresReason ? (
          <ActionConfirmationModal
            actionConfig={ACTION_CONFIGS.EXPORT_FLAGGED_PATH}
            onConfirm={(reason) => handleExport(reason)}
            buttonProps={{ disabled: newFlaggedPathsForExport.length === 0 }}
          >
            <Button disabled={newFlaggedPathsForExport.length === 0}>Export CSV</Button>
          </ActionConfirmationModal>
        ) : (
          <Button
            disabled={newFlaggedPathsForExport.length === 0}
            onClick={() => setConfirmExportModalOpen(true)}
          >
            Export CSV
          </Button>
        )}
      </div>

      <div className={drawerTableWrapperClassName}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[85%] p-3">Path</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {newFlaggedPathsForExport.map((flaggedPath) => (
              <TableRow key={flaggedPath.id}>
                <TableCell>
                  <span className="text-sm break-all">{flaggedPath.path}</span>
                </TableCell>
                <TableCell className="p-3 flex justify-end items-center">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    aria-label="Remove"
                    onClick={() => {
                      setNewFlaggedPathsForExport((prev) =>
                        prev.filter((p) => p.id !== flaggedPath.id)
                      );
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {confirmExportModalOpen && (
        <ConfirmationModal
          open={confirmExportModalOpen}
          onClose={() => setConfirmExportModalOpen(false)}
          onConfirm={() => handleExport()}
          message={`Are you sure you want to export CSV for ${newFlaggedPathsForExport.length} flagged path(s)?`}
          statusColor="primary"
          buttonText="Confirm"
        />
      )}
    </div>
  );
};

export default CreateExportFlaggedPathsToCsvTable;
